import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AcademicYearManagement from './AcademicYearManagement';
import BatchManagement from './BatchManagement.js';
import DepartmentManagement from './DepartmentManagement';
import SemesterManagement from './SemesterManagement';
import ProgramManagement from './ProgramManagement';
import FacultyManagement from './FacultyManagement';
import CourseManagementAdmin from './CourseManagementAdmin';
import StudentManagement from './StudentManagement';
import AdminReports from './AdminReports';
import CertificateManagement from './CertificateManagement';

const AdminDashboard = () => {
  const [activeView, setActiveView] = useState('Dashboard');
  const [stats, setStats] = useState({
    students: 0,
    faculty: 0,
    courses: 0,
    enrollments: 0,
    completionRate: 0,
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);

  const [sendingNotification, setSendingNotification] = useState(false);
  const [notificationError, setNotificationError] = useState('');
  const [notificationSuccess, setNotificationSuccess] = useState('');
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    message: '',
    target: 'both',
    notification_type: 'announcement',
    recipient_mode: 'all',
    faculty_id: '',
    student_id: '',
  });
  const [facultyOptions, setFacultyOptions] = useState([]);
  const [studentOptions, setStudentOptions] = useState([]);

  const navItems = [
    { icon: '📊', label: 'Dashboard', key: 'Dashboard' },
    { icon: '📅', label: 'Academic Year', key: 'AcademicYear' },
    { icon: '📚', label: 'Semester', key: 'Semester' },
    { icon: '👥', label: 'Batch', key: 'Batch' },
    { icon: '🏢', label: 'Department', key: 'Department' },
    { icon: '🎓', label: 'Program', key: 'Program' },
    { icon: '👨‍🏫', label: 'Faculty', key: 'Faculty' },
    { icon: '📖', label: 'Courses', key: 'Course' },
    { icon: '👩‍🎓', label: 'Students', key: 'Students' },
    { icon: '🔔', label: 'Notifications', key: 'Notification' },
    { icon: '�', label: 'Certificates', key: 'Certificates' },
    { icon: '�📋', label: 'Reports', key: 'Reports' },
  ];

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      localStorage.removeItem('user');
      window.location.href = '/';
    }
  };

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/dashboard-stats', { timeout: 10000 });
      if (res.data) {
        setStats({
          students: res.data.students || 0,
          faculty: res.data.faculty || 0,
          courses: res.data.courses || 0,
          enrollments: res.data.enrollments || 0,
          completionRate: res.data.completionRate || 0,
        });
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleNavClick = (key) => {
    setActiveView(key);
  };
  const resetNotificationForm = () => {
    setNotificationForm({
      title: '',
      message: '',
      target: 'both',
      notification_type: 'announcement',
      recipient_mode: 'all',
      faculty_id: '',
      student_id: '',
    });
  };

  const handleNotificationFormChange = (e) => {
    const { name, value } = e.target;
    setNotificationForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'target') {
        if (value === 'both') {
          next.recipient_mode = 'all';
        }
        next.faculty_id = '';
        next.student_id = '';
      }
      if (name === 'recipient_mode') {
        next.faculty_id = '';
        next.student_id = '';
      }
      return next;
    });
  };

  useEffect(() => {
    const loadRecipients = async () => {
      if (activeView !== 'Notification') return;
      try {
        setHistoryLoading(true);
        const [facRes, stuRes] = await Promise.all([
          axios.get('/api/faculties'),
          axios.get('/api/students'),
        ]);
        setFacultyOptions(Array.isArray(facRes.data) ? facRes.data : []);
        setStudentOptions(stuRes.data?.students || []);
        const historyRes = await axios.get('/api/admin/notifications/history?limit=30&offset=0');
        setNotificationHistory(historyRes.data?.history || []);
      } catch (err) {
        console.error('Failed to load recipient lists:', err);
      } finally {
        setHistoryLoading(false);
      }
    };
    loadRecipients();
  }, [activeView]);

  const handleSendNotification = async (e) => {
    e.preventDefault();
    setNotificationError('');
    setNotificationSuccess('');

    if (!notificationForm.title.trim()) {
      setNotificationError('Notification title is required');
      return;
    }
    if (!notificationForm.message.trim()) {
      setNotificationError('Notification message is required');
      return;
    }
    if (notificationForm.target !== 'both' && notificationForm.recipient_mode === 'single') {
      if (notificationForm.target === 'faculty' && !notificationForm.faculty_id) {
        setNotificationError('Please select a faculty');
        return;
      }
      if (notificationForm.target === 'student' && !notificationForm.student_id) {
        setNotificationError('Please select a student');
        return;
      }
    }

    setSendingNotification(true);
    try {
      const res = await axios.post('/api/admin/notifications/send', {
        title: notificationForm.title.trim(),
        message: notificationForm.message.trim(),
        target: notificationForm.target,
        notification_type: notificationForm.notification_type,
        recipient_mode: notificationForm.target === 'both' ? 'all' : notificationForm.recipient_mode,
        faculty_id: notificationForm.faculty_id || null,
        student_id: notificationForm.student_id || null,
      });
      const total = res.data?.total_recipients || 0;
      setNotificationSuccess(`Notification sent to ${total} recipients.`);
      try {
        const historyRes = await axios.get('/api/admin/notifications/history?limit=30&offset=0');
        setNotificationHistory(historyRes.data?.history || []);
      } catch (historyErr) {
        console.error('Failed to refresh notification history:', historyErr);
      }
      resetNotificationForm();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to send notification';
      setNotificationError(msg);
    } finally {
      setSendingNotification(false);
    }
  };

  const renderActiveView = () => {
    switch (activeView) {
      case 'Dashboard':
        return <DashboardView stats={stats} loading={loading} />;
      case 'AcademicYear':
        return <AcademicYearManagement />;
      case 'Semester':
        return <SemesterManagement />;
      case 'Batch':
        return <BatchManagement />;
      case 'Department':
        return <DepartmentManagement />;
      case 'Program':
        return <ProgramManagement />;
      case 'Faculty':
        return <FacultyManagement />;
      case 'Course':
        return <CourseManagementAdmin />;
      case 'Students':
        return <StudentManagement />;
      case 'Notification':
        return (
          <NotificationPage
            notificationForm={notificationForm}
            handleNotificationFormChange={handleNotificationFormChange}
            handleSendNotification={handleSendNotification}
            sendingNotification={sendingNotification}
            notificationError={notificationError}
            notificationSuccess={notificationSuccess}
            facultyOptions={facultyOptions}
            studentOptions={studentOptions}
            notificationHistory={notificationHistory}
            historyLoading={historyLoading}
          />
        );
      case 'Certificates':
        return <CertificateManagement />;
      case 'Reports':
        return <AdminReports />;
      default:
        return (
          <div style={placeholderStyle}>
            <span style={placeholderIconStyle}>🚧</span>
            <h2 style={placeholderTitleStyle}>{activeView} Management</h2>
            <p style={placeholderTextStyle}>Module under development</p>
          </div>
        );
    }
  };

  return (
    <div style={containerStyle}>
      <div style={sidebarStyle(sidebarCollapsed)}>
        <div style={logoSectionStyle}>
          <span style={logoIconStyle}>🎓</span>
          {!sidebarCollapsed && (
            <h2 style={logoTextStyle}>
              RCMAS RE-LEARNING
              <br />
              PLATFORM
            </h2>
          )}
          <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} style={collapseBtnStyle}>
            {sidebarCollapsed ? '▶️' : '◀️'}
          </button>
        </div>

        <nav style={navStyle}>
          {navItems.map((item, index) => (
            <button key={index} onClick={() => handleNavClick(item.key)} style={navLinkStyle(activeView === item.key)}>
              <span style={navIconStyle(activeView === item.key)}>{item.icon}</span>
              {!sidebarCollapsed && <span style={navLabelStyle}>{item.label}</span>}
            </button>
          ))}
        </nav>

        <div style={sidebarBottomStyle}>
          <button onClick={handleLogout} style={logoutBtnStyle}>
            <span>🚪</span>
            {!sidebarCollapsed && <span>Logout</span>}
          </button>
        </div>
      </div>

      <div style={mainContentStyle}>
        <header style={topBarStyle}>
          <div style={topLeftStyle}>
            <span style={menuIconStyle}>☰</span>
            <div>
              <h1 style={pageTitleStyle}>{getPageTitle(activeView)}</h1>
              {activeView !== 'Dashboard' && (
                <p style={pageSubtitleStyle}>
                  {stats.courses || 0} courses | {stats.students || 0} registered students
                </p>
              )}
            </div>
          </div>
          <div style={topRightStyle}>
            <div style={userProfileStyle}>
              <span style={userNameStyle}>Administrator</span>
              <div style={avatarStyle}>A</div>
            </div>
          </div>
        </header>

        <main style={contentBodyStyle}>{renderActiveView()}</main>
      </div>

    </div>
  );
};

const getPageTitle = (activeView) => {
  const titles = {
    Dashboard: 'WELCOME TO ADMIN DASHBOARD',
    AcademicYear: 'Academic Year Management',
    Semester: 'Semester Management',
    Batch: 'Batch Management',
    Department: 'Department Management',
    Program: 'Program Management',
    Faculty: 'Faculty Management',
    Course: 'Course Management',
    Students: 'Student Management',
    Notification: 'Notifications',
    Reports: 'Reports & Analytics',
  };
  return titles[activeView] || `${activeView} Management`;
};

const DashboardView = ({ stats, loading }) => (
  <div style={dashboardViewStyle}>
    {loading && stats.students === 0 ? (
      <div style={{ textAlign: 'center', padding: '40px' }}>Refreshing statistics...</div>
    ) : (
      <div style={statsGridStyle}>
        <StatCard icon="👨‍🎓" title="Total Students" value={stats.students} color="#3b82f6" />
        <StatCard icon="👨‍🏫" title="Total Faculty" value={stats.faculty} color="#10b981" />
        <StatCard icon="📚" title="Total Courses" value={stats.courses} color="#f59e0b" />
        <StatCard icon="📈" title="Enrollments" value={stats.enrollments} color="#ef4444" />
      </div>
    )}

    <div style={{ marginTop: '40px' }}>
      <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', marginBottom: '20px' }}>System Overview</h2>
      <div style={{ background: '#fff', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
        <p style={{ color: '#64748b' }}>
          Welcome to the RCMAS RE-LEARNING PLATFORM administration panel. Use the sidebar to manage academic records,
          faculty, and student data.
        </p>
      </div>
    </div>
  </div>
);

const NotificationPage = ({
  notificationForm,
  handleNotificationFormChange,
  handleSendNotification,
  sendingNotification,
  notificationError,
  notificationSuccess,
  facultyOptions,
  studentOptions,
  notificationHistory,
  historyLoading,
}) => (
  <div style={notificationPageWrapStyle}>
    <div style={notificationGridStyle}>
      <div style={notificationPanelStyle}>
        <h2 style={modalTitleStyle}>Send Notification</h2>
        <form onSubmit={handleSendNotification} style={modalFormStyle}>
          <label style={modalLabelStyle}>Send To</label>
          <select name="target" value={notificationForm.target} onChange={handleNotificationFormChange} style={modalInputStyle}>
            <option value="faculty">Faculty</option>
            <option value="student">Students</option>
            <option value="both">Faculty + Students</option>
          </select>

          {notificationForm.target !== 'both' && (
            <>
              <label style={modalLabelStyle}>Recipients</label>
              <select name="recipient_mode" value={notificationForm.recipient_mode} onChange={handleNotificationFormChange} style={modalInputStyle}>
                <option value="all">All</option>
                <option value="single">Single</option>
              </select>
            </>
          )}

          {notificationForm.target === 'faculty' && notificationForm.recipient_mode === 'single' && (
            <>
              <label style={modalLabelStyle}>Select Faculty</label>
              <select name="faculty_id" value={notificationForm.faculty_id} onChange={handleNotificationFormChange} style={modalInputStyle}>
                <option value="">-- Select Faculty --</option>
                {facultyOptions.map((fac) => (
                  <option key={fac.fac_id} value={fac.fac_id}>
                    {fac.fac_name || fac.faculty_name} ({fac.fac_id})
                  </option>
                ))}
              </select>
            </>
          )}

          {notificationForm.target === 'student' && notificationForm.recipient_mode === 'single' && (
            <>
              <label style={modalLabelStyle}>Select Student</label>
              <select name="student_id" value={notificationForm.student_id} onChange={handleNotificationFormChange} style={modalInputStyle}>
                <option value="">-- Select Student --</option>
                {studentOptions.map((std) => (
                  <option key={std.student_id} value={std.student_id}>
                    {std.student_name} ({std.student_id})
                  </option>
                ))}
              </select>
            </>
          )}

          <label style={modalLabelStyle}>Type</label>
          <select name="notification_type" value={notificationForm.notification_type} onChange={handleNotificationFormChange} style={modalInputStyle}>
            <option value="announcement">Announcement</option>
            <option value="alert">Alert</option>
            <option value="message">Message</option>
          </select>

          <label style={modalLabelStyle}>Title</label>
          <input name="title" type="text" value={notificationForm.title} onChange={handleNotificationFormChange} placeholder="Enter notification title" style={modalInputStyle} />

          <label style={modalLabelStyle}>Message</label>
          <textarea name="message" value={notificationForm.message} onChange={handleNotificationFormChange} placeholder="Enter message" rows={5} style={modalTextareaStyle} />

          {notificationError && <div style={modalErrorStyle}>{notificationError}</div>}
          {notificationSuccess && <div style={modalSuccessStyle}>{notificationSuccess}</div>}

          <div style={modalActionsStyle}>
            <button type="submit" style={modalSendBtnStyle} disabled={sendingNotification}>
              {sendingNotification ? 'Sending...' : 'Send Notification'}
            </button>
          </div>
        </form>
      </div>

      <div style={notificationPanelStyle}>
        <div style={historySectionStyle}>
          <h3 style={historyTitleStyle}>Notification History</h3>
          {historyLoading ? (
            <div style={historyEmptyStyle}>Loading history...</div>
          ) : notificationHistory.length === 0 ? (
            <div style={historyEmptyStyle}>No notifications sent yet.</div>
          ) : (
            <div style={historyListStyle}>
              {notificationHistory.map((item) => (
                <div key={item.history_id} style={historyCardStyle}>
                  <div style={historyCardHeaderStyle}>
                    <strong>{item.title}</strong>
                    <span style={historyMetaStyle}>{new Date(item.created_at).toLocaleString()} | {item.audience}</span>
                  </div>
                  <div style={historyMessageStyle}>{item.message}</div>
                  <div style={historyStatsStyle}>
                    Sent: {item.total_recipients} | Viewed: {item.viewed_count} | Pending: {item.pending_count}
                  </div>
                  <div style={viewerTitleStyle}>Viewed By:</div>
                  {item.viewed_by && item.viewed_by.length > 0 ? (
                    <div style={viewerListStyle}>
                      {item.viewed_by.map((viewer) => (
                        <div key={`${item.history_id}-${viewer.id}`} style={viewerItemStyle}>
                          {viewer.name} ({viewer.id})
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={viewerNoneStyle}>No one has viewed this yet.</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

const StatCard = ({ icon, title, value, color }) => (
  <div style={statCardStyle(color)}>
    <div style={statIconStyle}>{icon}</div>
    <h3 style={statNumberStyle}>{value.toLocaleString()}</h3>
    <p style={statLabelStyle}>{title}</p>
  </div>
);

const containerStyle = { display: 'flex', height: '100vh', background: '#f8fafc', fontFamily: 'system-ui, -apple-system, sans-serif' };
const sidebarStyle = (collapsed) => ({ background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)', color: '#e2e8f0', display: 'flex', flexDirection: 'column', width: collapsed ? '72px' : '260px', transition: 'width 0.3s ease', boxShadow: '4px 0 12px rgba(0,0,0,0.1)', overflow: 'hidden', flexShrink: 0 });
const logoSectionStyle = { display: 'flex', alignItems: 'center', padding: '20px 16px', borderBottom: '1px solid rgba(148, 163, 184, 0.1)', gap: '10px', minHeight: '78px', boxSizing: 'border-box' };
const logoIconStyle = { fontSize: '28px' };
const logoTextStyle = { fontSize: '16px', fontWeight: '700', color: '#fff', margin: 0, lineHeight: 1.15, whiteSpace: 'normal' };
const collapseBtnStyle = { marginLeft: 'auto', background: 'rgba(148, 163, 184, 0.2)', border: 'none', color: '#94a3b8', width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const navStyle = { flex: 1, padding: '14px 0', overflowY: 'auto' };
const navLinkStyle = (isActive) => ({ display: 'flex', alignItems: 'center', gap: '12px', height: '46px', padding: '0 16px', color: isActive ? '#fff' : '#94a3b8', background: isActive ? 'linear-gradient(135deg, #1e3a8a, #0f766e)' : 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer', fontWeight: isActive ? '600' : '500', transition: 'all 0.2s', boxSizing: 'border-box' });
const navIconStyle = () => ({ fontSize: '18px', width: '24px', minWidth: '24px', textAlign: 'center', lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' });
const navLabelStyle = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 };
const sidebarBottomStyle = { padding: '20px', borderTop: '1px solid rgba(148, 163, 184, 0.1)', display: 'flex', flexDirection: 'column', gap: '12px' };
const logoutBtnStyle = { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: 'rgba(239, 68, 68, 0.1)', color: '#f87171', border: 'none', borderRadius: '8px', cursor: 'pointer' };
const mainContentStyle = { flex: 1, display: 'flex', flexDirection: 'column', background: '#f8fafc', overflow: 'hidden' };
const topBarStyle = { height: '72px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px', borderBottom: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', flexShrink: 0 };
const topLeftStyle = { display: 'flex', alignItems: 'center', gap: '24px' };
const menuIconStyle = { fontSize: '24px', color: '#64748b', cursor: 'pointer' };
const pageTitleStyle = { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: '0 0 4px 0' };
const pageSubtitleStyle = { fontSize: '14px', color: '#64748b', margin: 0 };
const topRightStyle = { display: 'flex', alignItems: 'center', gap: '20px' };
const userProfileStyle = { display: 'flex', alignItems: 'center', gap: '12px' };
const userNameStyle = { fontSize: '14px', color: '#64748b', fontWeight: '500' };
const avatarStyle = { width: '40px', height: '40px', background: '#1e3a8a', color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '600', fontSize: '16px' };
const contentBodyStyle = { flex: 1, padding: '32px', overflowY: 'auto' };
const dashboardViewStyle = { maxWidth: '1400px', margin: '0 auto' };
const statsGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' };
const statCardStyle = (color) => ({ background: '#fff', padding: '32px 24px', borderRadius: '16px', textAlign: 'center', border: '1px solid #e2e8f0', borderLeft: `4px solid ${color}`, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', transition: 'transform 0.2s ease' });
const statIconStyle = { fontSize: '32px', marginBottom: '16px' };
const statNumberStyle = { fontSize: '36px', fontWeight: '700', color: '#1e293b', margin: '0 0 8px 0' };
const statLabelStyle = { fontSize: '14px', color: '#64748b', margin: 0, fontWeight: '500' };
const placeholderStyle = { background: '#fff', padding: '80px 40px', borderRadius: '20px', textAlign: 'center', maxWidth: '600px', margin: '0 auto', boxShadow: '0 10px 25px rgba(0,0,0,0.08)' };
const placeholderIconStyle = { fontSize: '48px', display: 'block', marginBottom: '20px' };
const placeholderTitleStyle = { fontSize: '22px', fontWeight: '600', color: '#1e293b', margin: '0 0 8px 0' };
const placeholderTextStyle = { fontSize: '16px', color: '#64748b', margin: 0 };
const modalTitleStyle = { margin: '0 0 14px 0', fontSize: '22px', color: '#0f172a' };
const modalFormStyle = { display: 'flex', flexDirection: 'column', gap: '10px' };
const modalLabelStyle = { fontSize: '13px', fontWeight: '700', color: '#334155' };
const modalInputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' };
const modalTextareaStyle = { width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', resize: 'vertical' };
const modalActionsStyle = { marginTop: '6px', display: 'flex', justifyContent: 'flex-end', gap: '10px' };
const modalCancelBtnStyle = { padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#fff', color: '#334155', cursor: 'pointer' };
const modalSendBtnStyle = { padding: '10px 14px', border: 'none', borderRadius: '8px', background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: '700' };
const modalErrorStyle = { background: '#fee2e2', color: '#991b1b', padding: '10px 12px', borderRadius: '8px', fontSize: '13px' };
const modalSuccessStyle = { background: '#dcfce7', color: '#166534', padding: '10px 12px', borderRadius: '8px', fontSize: '13px' };
const historySectionStyle = { marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '14px' };
const historyTitleStyle = { margin: '0 0 10px 0', fontSize: '16px', color: '#0f172a' };
const historyEmptyStyle = { padding: '10px 0', color: '#64748b', fontSize: '14px' };
const historyListStyle = { maxHeight: '260px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' };
const historyCardStyle = { border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px', background: '#f8fafc' };
const historyCardHeaderStyle = { display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'baseline' };
const historyMetaStyle = { fontSize: '12px', color: '#64748b' };
const historyMessageStyle = { marginTop: '6px', color: '#334155', fontSize: '14px' };
const historyStatsStyle = { marginTop: '8px', fontSize: '13px', color: '#0f172a', fontWeight: '600' };
const viewerTitleStyle = { marginTop: '8px', fontSize: '12px', color: '#475569', fontWeight: '700' };
const viewerListStyle = { display: 'flex', flexDirection: 'column', gap: '3px', marginTop: '4px' };
const viewerItemStyle = { fontSize: '12px', color: '#334155' };
const viewerNoneStyle = { marginTop: '4px', fontSize: '12px', color: '#94a3b8' };
const notificationPageWrapStyle = { maxWidth: '1400px', margin: '0 auto' };
const notificationGridStyle = { display: 'grid', gridTemplateColumns: 'minmax(360px, 460px) 1fr', gap: '20px', alignItems: 'start' };
const notificationPanelStyle = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '18px' };

export default AdminDashboard;
